/**
 * /practice-sale/buy/:token — the buyer's page for a practice sale (design
 * docs/plans/2026-11-04-practice-sale-licence-transfer-design.md sections 6 and 7;
 * build job mct_e446474b-67c, PR 2). The link rides in the email we send the seller,
 * who forwards it to the buyer; the token is the whole authority.
 *
 * Three steps, in order, and the Engine decides which one shows (buyPageState):
 *   1. book the call with our team (Michael, 4 Nov: the meeting is part of the
 *      offer, booked before buying) — the order never opens before it;
 *   2. tick the terms (kept word for word with the time);
 *   3. choose Option A or B, and go on to our own order page with the code applied.
 * Never a Spiffy link: the order page is ours (/checkout/<plan>).
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BRAND_NAME } from './branding';
import { useNoIndex } from './lib/useNoIndex';

const API = import.meta.env.VITE_API_BASE_URL as string;
const TSB_LOGO = 'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/brands/tsb/logo-light.svg';

interface BuyData {
  ok: boolean;
  state: 'closed' | 'needs_booking' | 'needs_terms' | 'open' | 'bought';
  buyer_first_name: string | null;
  practice: string | null;
  booking_url: string | null;
  terms_words: string | null;
  terms_at: string | null;
  option_a: { plan_label: string; price: string; code: string } | null;
  /** Each BKOS plan has its own code (Practice 20% off, Firm 30% off). */
  option_b: { plans: Array<{ plan: 'practice' | 'firm'; label: string; price: string; code: string }>; discount: string | null } | null;
}

export function PracticeSaleBuyPage() {
  const { token = '' } = useParams<{ token: string }>();
  useNoIndex(true);
  const [data, setData] = useState<BuyData | null>(null);
  const [problem, setProblem] = useState<'link' | 'network' | null>(null);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = `Taking over the practice · ${BRAND_NAME}`;
    return () => { document.title = BRAND_NAME; };
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/public/practice-sale/buy/${encodeURIComponent(token)}`);
      if (!r.ok) { setProblem('link'); return; }
      setData(await r.json() as BuyData);
      setProblem(null);
    } catch {
      setProblem('network');
    }
  }, [token]);
  useEffect(() => { void load(); }, [load]);

  async function go(option: 'a' | 'b', plan?: 'practice' | 'firm') {
    setError(null);
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/public/practice-sale/buy/${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ terms: true, option, plan }),
      });
      const d = await r.json().catch(() => ({})) as { ok?: boolean; error?: string; checkout_url?: string };
      if (r.ok && d.ok && d.checkout_url) { window.location.href = d.checkout_url; return; }
      setError(d.error || 'Something went wrong. Please try again, or reply to the email and we will help.');
      void load();
    } catch {
      setError('We could not reach our servers. Please check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  const first = data?.buyer_first_name;
  const canBuy = data && (data.state === 'needs_terms' || data.state === 'open');
  const ticked = terms || !!data?.terms_at;
  return (
    <div style={wrap}>
      <header style={nav}>
        <div style={navInner}>
          <a href="https://www.thesuccessfulbookkeeper.com"><img src={TSB_LOGO} alt="The Successful Bookkeeper" style={{ height: 32 }} /></a>
        </div>
      </header>
      <main style={main}>
        <p style={eyebrow}>Taking over {data?.practice || 'the practice'}</p>
        <h1 style={h1}>Hi{first ? ` ${first}` : ''}, welcome to The Successful Bookkeeper</h1>
        {problem === 'link' && <section style={card}><p style={p0}>This link is not complete. Please use the link in the email the seller passed on to you, or reply to that email and we'll help.</p></section>}
        {problem === 'network' && <section style={card}><p style={p0}>We could not reach our servers. Please check your connection and reload this page.</p></section>}
        {!problem && !data && <section style={card}><p style={{ ...p0, opacity: 0.75 }}>One moment…</p></section>}
        {data?.state === 'closed' && (
          <section style={card}><p style={p0}>This offer is no longer open. Please reply to the email the seller passed on to you and we'll sort it out with you.</p></section>
        )}
        {data?.state === 'bought' && (
          <section style={card}>
            <p style={p0}><b>Thank you, your order is in.</b></p>
            <p style={{ ...p0, marginTop: 10 }}>We'll be in touch as we hand the practice's membership and community account over to you.</p>
          </section>
        )}

        {data?.state === 'needs_booking' && (
          <section style={card}>
            <h2 style={h2}>Step 1: book a short call with us</h2>
            <p style={p0}>
              Before you buy, we'd love to meet you. On the call we'll welcome you, walk through your options, and make sure the team and the
              community account carry across smoothly. Please book with the same email address the seller gave us.
            </p>
            {data.booking_url && <p style={{ ...p0, marginTop: 14 }}><a href={data.booking_url} style={btnLink}>Book the call</a></p>}
            <p style={fine}>Already booked? <button type="button" onClick={() => void load()} style={linkBtn}>Check again</button></p>
          </section>
        )}

        {(data?.option_a || data?.option_b) && data?.state !== 'bought' && data?.state !== 'closed' && (
          <>
            {data.state === 'needs_booking' && <p style={lede}>Here is what's on offer. The order opens once your call is booked.</p>}
            {canBuy && data.terms_words && (
              <section style={card}>
                <h2 style={h2}>Step 2: the terms</h2>
                <label style={{ ...radio, alignItems: 'flex-start' }}>
                  <input type="checkbox" checked={ticked} disabled={!!data.terms_at} onChange={(e) => setTerms(e.target.checked)} style={{ marginTop: 4 }} />
                  <span>{data.terms_words}</span>
                </label>
              </section>
            )}
            {data.option_a && (
              <section style={card}>
                <h2 style={h2}>Option A: carry on as the practice is</h2>
                <p style={p0}>{data.option_a.plan_label}, everything the practice has today, at {data.option_a.price}. Your code: <b>{data.option_a.code}</b></p>
                {canBuy && (
                  <button type="button" style={{ ...btn, marginTop: 14, opacity: ticked ? 1 : 0.5 }} disabled={!ticked || busy} onClick={() => void go('a')}>
                    {busy ? 'One moment…' : `Continue with ${data.option_a.plan_label}`}
                  </button>
                )}
              </section>
            )}
            {data.option_b && (
              <section style={card}>
                <h2 style={h2}>Option B: step up to BKOS</h2>
                <p style={p0}>
                  These include Member Pro, plus the software to run the practice. Each plan has its own code.
                </p>
                {data.option_b.plans.map((p) => (
                  <div key={p.plan} style={{ marginTop: 12 }}>
                    <div><b>{p.label}</b>: {p.price}. Your code: <b>{p.code}</b></div>
                    {canBuy && (
                      <button type="button" style={{ ...btn, marginTop: 8, opacity: ticked ? 1 : 0.5 }} disabled={!ticked || busy} onClick={() => void go('b', p.plan)}>
                        {busy ? 'One moment…' : `Continue with ${p.label}`}
                      </button>
                    )}
                  </div>
                ))}
              </section>
            )}
            {canBuy && !ticked && <p style={fine}>Tick the terms above to continue.</p>}
          </>
        )}
        {error && <p style={{ color: '#b42318', margin: '0 0 12px' }}>{error}</p>}
        <p style={fine}>Questions? Reply to the email the seller passed on to you and a real person on our team will help.</p>
      </main>
    </div>
  );
}

const INK = '#0A0A2E';
const wrap: React.CSSProperties = { minHeight: '100vh', background: '#F8F6F3', color: INK, fontFamily: 'var(--font-body, "Poppins", -apple-system, sans-serif)' };
const nav: React.CSSProperties = { padding: '12px 24px', borderBottom: '1px solid rgba(10,10,46,0.08)', background: '#fff' };
const navInner: React.CSSProperties = { maxWidth: 760, margin: '0 auto', display: 'flex', alignItems: 'center' };
const main: React.CSSProperties = { maxWidth: 760, margin: '0 auto', padding: '40px 16px 72px' };
const eyebrow: React.CSSProperties = { textTransform: 'uppercase', letterSpacing: 1.2, fontSize: 12, fontWeight: 600, opacity: 0.6, margin: '0 0 10px' };
const h1: React.CSSProperties = { fontFamily: 'var(--font-heading, "Fraunces", Georgia, serif)', fontSize: 'clamp(28px, 4.2vw, 38px)', fontWeight: 600, lineHeight: 1.15, margin: '0 0 16px' };
const h2: React.CSSProperties = { fontSize: 18, fontWeight: 600, margin: '0 0 12px' };
const lede: React.CSSProperties = { fontSize: 17, lineHeight: 1.6, opacity: 0.85, margin: '0 0 18px' };
const card: React.CSSProperties = { background: '#fff', borderRadius: 10, padding: '22px 24px', marginBottom: 18, borderLeft: '4px solid var(--prog-primary, #FF6B35)', lineHeight: 1.55 };
const p0: React.CSSProperties = { margin: 0 };
const radio: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', fontSize: 15, marginTop: 6 };
const btn: React.CSSProperties = { fontSize: 16, fontWeight: 600, padding: '12px 22px', borderRadius: 8, border: 'none', background: 'var(--prog-primary, #FF6B35)', color: '#fff', cursor: 'pointer' };
const btnLink: React.CSSProperties = { ...btn, display: 'inline-block', textDecoration: 'none' };
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, color: INK, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' };
const fine: React.CSSProperties = { fontSize: 13, opacity: 0.7, margin: '14px 0 0', lineHeight: 1.5 };

export default PracticeSaleBuyPage;
